/**
 * Right-side task detail drawer — replaces the old EditTaskDialog.
 *
 * Sections:
 *   - Header: task id + editable title + status/priority chips.
 *   - Properties: assignee / due date / phase / evidence folder.
 *   - Description (persists on blur).
 *   - Checklist (toggle / add / remove items).
 *   - Dependencies (link / unlink with blocker flag).
 *   - Activity timeline (merged comments + events) with comment composer.
 */
import { Eye, FileText, Link as LinkIcon, Paperclip, Trash2, UploadCloud, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import type { TradeCategory } from "@/hooks/use-config";
import { useContacts, useProjectContacts } from "@/hooks/use-contacts";
import { useProjectFolders } from "@/hooks/use-folders";
import type { ChecklistItem, ProjectTask, TaskActivityEntry, TaskFile } from "@/hooks/use-tasks";
import {
  useAddChecklistItem,
  useAddTaskDependency,
  useDeleteTaskFile,
  usePostTaskComment,
  useRemoveChecklistItem,
  useRemoveTaskDependency,
  useTaskActivity,
  useTaskChecklist,
  useTaskComments,
  useTaskDependencies,
  useTaskFiles,
  useToggleChecklistItem,
  useUpdateProjectTask,
  useUploadTaskEvidence,
} from "@/hooks/use-tasks";
import { ContactPicker, TradePicker } from "./assignee-picker";
import {
  PhaseChip,
  PriorityChip,
  type ProjectPhase,
  StatusChip,
  TaskAvatar,
  type TaskPriority,
  type TaskStatus,
} from "./task-chips";

// ── Helpers ──────────────────────────────────────────────────────────

function fullName(name: string | null): string {
  return name?.trim() || "Someone";
}

function relativeTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function activityLabel(kind: string): string {
  switch (kind) {
    case "created":
      return "created this task";
    case "status_changed":
      return "changed status";
    case "priority_changed":
      return "changed priority";
    case "assignee_changed":
      return "changed assignee";
    case "due_changed":
      return "changed due date";
    case "progress_changed":
      return "updated progress";
    case "dependency_linked":
      return "linked a dependency";
    case "dependency_unlinked":
      return "removed a dependency";
    case "checklist_toggled":
      return "toggled a checklist item";
    case "comment_added":
      return "added a comment";
    default:
      return kind;
  }
}

function renderMentions(body: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let key = 0;
  for (const match of body.matchAll(/(@[A-Z][a-zA-Z]+)/g)) {
    const idx = match.index ?? 0;
    if (idx > lastIdx) parts.push(body.slice(lastIdx, idx));
    parts.push(
      <span key={`m-${key++}`} className="mention">
        {match[0]}
      </span>,
    );
    lastIdx = idx + match[0].length;
  }
  if (lastIdx < body.length) parts.push(body.slice(lastIdx));
  return parts;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Section components ────────────────────────────────────────────────

function EvidenceSection({
  projectId,
  taskId,
  folderId,
}: {
  projectId: string;
  taskId: string;
  folderId: string | null;
}) {
  const { data: taskFiles } = useTaskFiles(projectId, taskId);
  const upload = useUploadTaskEvidence(projectId, taskId);
  const del = useDeleteTaskFile(projectId, taskId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const files: TaskFile[] = taskFiles ?? [];

  function handleFiles(list: FileList) {
    for (const f of Array.from(list)) {
      upload.mutate({ file: f, folderId: folderId ?? undefined });
    }
  }

  return (
    <div className="sec">
      <div className="sec-head">
        <span>Attachments</span>
        <span className="count">{files.length}</span>
      </div>

      {!folderId ? (
        <p className="ev-no-folder">Select an evidence folder above to enable file uploads.</p>
      ) : (
        <>
          {/* Drop zone */}
          <button
            type="button"
            className={`ev-drop${dragOver ? " is-over" : ""}`}
            aria-label="Upload files"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
            }}
          >
            <UploadCloud className="h-5 w-5" />
            <span>{upload.isPending ? "Uploading…" : "Drop files here or click to browse"}</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="sr-only"
            onChange={(e) => {
              if (e.target.files?.length) handleFiles(e.target.files);
              // Reset so the same file can be re-uploaded
              e.target.value = "";
            }}
          />

          {/* File list */}
          {files.length > 0 ? (
            <div className="ev-list">
              {files.map((f) => {
                const isImage = (f.mimeType ?? "").startsWith("image/");
                const uploader =
                  [f.uploadedByFirstName, f.uploadedByLastName].filter(Boolean).join(" ") || "—";
                const sizeLabel = f.fileSizeBytes != null ? formatBytes(f.fileSizeBytes) : null;
                return (
                  <div key={f.id} className="ev-row">
                    {isImage ? (
                      <img src={`/api/files/${f.id}`} alt={f.fileName} className="ev-thumb" />
                    ) : (
                      <span className="ev-icon">
                        <FileText className="h-4 w-4 text-[var(--text-4)]" />
                      </span>
                    )}
                    <div className="ev-meta">
                      <span className="ev-name" title={f.fileName}>
                        {f.fileName}
                      </span>
                      <span className="ev-detail">
                        {[sizeLabel, uploader, relativeTimestamp(f.createdAt)]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                    <a
                      href={`/api/files/${f.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ev-action"
                      aria-label="Preview"
                      title="Preview"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </a>
                    <button
                      type="button"
                      className="ev-action del"
                      aria-label="Delete file"
                      title="Delete file"
                      onClick={() => del.mutate(f.id)}
                      disabled={del.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function ChecklistSection({ projectId, taskId }: { projectId: string; taskId: string }) {
  const { data: items } = useTaskChecklist(projectId, taskId);
  const toggle = useToggleChecklistItem(projectId, taskId);
  const add = useAddChecklistItem(projectId, taskId);
  const remove = useRemoveChecklistItem(projectId, taskId);
  const [text, setText] = useState("");

  const rows: ChecklistItem[] = items ?? [];
  const doneCount = rows.filter((r) => r.done).length;

  function onAdd() {
    const trimmed = text.trim();
    if (!trimmed) return;
    add.mutate({ text: trimmed });
    setText("");
  }

  return (
    <div className="sec">
      <div className="sec-head">
        <span>Checklist</span>
        <span className="count">
          {doneCount}/{rows.length}
        </span>
      </div>
      <div className="sub-list">
        {rows.map((it) => (
          <div key={it.id} className={`sub-item ${it.done ? "done" : ""}`}>
            <button
              type="button"
              className="box"
              onClick={() => toggle.mutate({ itemId: it.id, done: !it.done })}
            >
              {it.done ? "✓" : ""}
            </button>
            <span className="txt">{it.text}</span>
            <button
              type="button"
              className="text-[var(--text-4)] hover:text-[var(--rose-ink)]"
              onClick={() => remove.mutate(it.id)}
              aria-label="Remove item"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onAdd();
          }}
          placeholder="Add item…"
          className="flex-1 rounded-md border border-[var(--border-2)] bg-[var(--paper-0)] px-2 py-1 text-[12.5px] outline-none"
        />
        <Button size="sm" variant="outline" onClick={onAdd} disabled={!text.trim()}>
          Add
        </Button>
      </div>
    </div>
  );
}

function DependenciesSection({
  projectId,
  taskId,
  allTasks,
}: {
  projectId: string;
  taskId: string;
  allTasks: ProjectTask[];
}) {
  const { data: deps } = useTaskDependencies(projectId, taskId);
  const add = useAddTaskDependency(projectId, taskId);
  const remove = useRemoveTaskDependency(projectId, taskId);
  const [pickValue, setPickValue] = useState("");
  const [isBlocker, setIsBlocker] = useState(false);

  const available = allTasks.filter(
    (t) => t.id !== taskId && !(deps ?? []).some((d) => d.dependsOnTaskId === t.id),
  );

  function onLink() {
    if (!pickValue) return;
    add.mutate({ dependsOnTaskId: pickValue, isBlocker });
    setPickValue("");
    setIsBlocker(false);
  }

  return (
    <div className="sec">
      <div className="sec-head">
        <span>Dependencies</span>
        <span className="count">{(deps ?? []).length}</span>
      </div>
      <div className="dep-list">
        {(deps ?? []).map((d) => (
          <div key={d.id} className={`dep-row ${d.isBlocker ? "is-blocker" : ""}`}>
            <LinkIcon className="h-3 w-3 text-[var(--text-4)]" />
            <span className="tid">{d.dependsOnTaskId.slice(0, 6).toUpperCase()}</span>
            <span className="tt">{d.dependsOnTitle ?? "—"}</span>
            {d.isBlocker ? (
              <span className="chip p-high">
                <span className="cdot" />
                Blocker
              </span>
            ) : null}
            <button
              type="button"
              className="text-[var(--text-4)] hover:text-[var(--rose-ink)]"
              onClick={() => remove.mutate(d.id)}
              aria-label="Unlink"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <select
          value={pickValue}
          onChange={(e) => setPickValue(e.target.value)}
          className="flex-1 rounded-md border border-[var(--border-2)] bg-[var(--paper-0)] px-2 py-1 text-[12.5px]"
        >
          <option value="">Link a task…</option>
          {available.map((t) => (
            <option key={t.id} value={t.id}>
              {t.id.slice(0, 6).toUpperCase()} · {t.title ?? "—"}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-[11.5px] text-[var(--text-3)]">
          <input
            type="checkbox"
            checked={isBlocker}
            onChange={(e) => setIsBlocker(e.target.checked)}
          />
          Blocker
        </label>
        <Button size="sm" variant="outline" onClick={onLink} disabled={!pickValue}>
          Link
        </Button>
      </div>
    </div>
  );
}

function ActivitySection({
  projectId,
  taskId,
  onClose,
}: {
  projectId: string;
  taskId: string;
  onClose: () => void;
}) {
  const { data: activity } = useTaskActivity(projectId, taskId);
  const { data: comments } = useTaskComments(projectId, taskId);
  const postComment = usePostTaskComment(projectId, taskId);
  const [draft, setDraft] = useState("");

  type TimelineItem =
    | { kind: "comment"; id: string; createdAt: string; who: string; body: string }
    | {
        kind: "event";
        id: string;
        createdAt: string;
        who: string;
        label: string;
        payload: Record<string, unknown> | null;
      };

  const merged: TimelineItem[] = [];
  for (const c of comments ?? []) {
    merged.push({
      kind: "comment",
      id: c.id,
      createdAt: c.createdAt,
      who: fullName(c.authorName),
      body: c.body,
    });
  }
  for (const a of (activity ?? []) as TaskActivityEntry[]) {
    if (a.kind === "comment_added") continue;
    merged.push({
      kind: "event",
      id: a.id,
      createdAt: a.createdAt,
      who: fullName(a.actorName),
      label: activityLabel(a.kind),
      payload: a.payload,
    });
  }
  merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  function onPost() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    postComment.mutate(trimmed);
    setDraft("");
  }

  return (
    <div className="sec">
      <div className="sec-head">
        <span>Activity</span>
        <span className="count">{merged.length}</span>
      </div>
      <div className="act-list">
        {merged.map((item) => {
          if (item.kind === "comment") {
            return (
              <div key={item.id} className="comment">
                <div className="c-top">
                  <TaskAvatar name={item.who} keyHint={item.who} />
                  <span className="c-name">{item.who}</span>
                  <span className="c-time">{relativeTimestamp(item.createdAt)}</span>
                </div>
                <div className="c-body">{renderMentions(item.body)}</div>
              </div>
            );
          }
          return (
            <div key={item.id} className="act-item">
              <span className="dot" />
              <div className="act-meta">
                <b>{item.who}</b> {item.label}
                <span className="t">· {relativeTimestamp(item.createdAt)}</span>
              </div>
            </div>
          );
        })}
        {merged.length === 0 ? (
          <p className="text-[11.5px] text-[var(--text-4)]">No activity yet.</p>
        ) : null}
      </div>
      <div className="comment-input mt-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a comment… use @Name to mention"
          rows={2}
        />
        <div className="flex justify-end gap-2 mt-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button size="sm" onClick={onPost} disabled={!draft.trim() || postComment.isPending}>
            Comment
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main drawer component ────────────────────────────────────────────

export function TaskDrawer({
  projectId,
  task,
  open,
  onOpenChange,
  allTasks,
  trades,
}: {
  projectId: string;
  task: ProjectTask | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allTasks: ProjectTask[];
  trades: TradeCategory[];
}) {
  const updateTask = useUpdateProjectTask(projectId);
  // Project contacts filtered to this project; falls back gracefully while loading.
  const { data: projectContacts } = useProjectContacts(projectId);
  const { data: allContacts } = useContacts();
  // Fall back to all contacts when no project-specific contact assignments exist yet.
  const contacts = projectContacts?.length ? projectContacts : (allContacts ?? []);
  const { data: folders } = useProjectFolders(projectId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const titleRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setTitle(task?.title ?? "");
    setDescription(task?.description ?? "");
  }, [task]);

  useEffect(() => {
    if (!open || !task) return;
    const el = titleRef.current;
    if (!el) return;
    const id = window.setTimeout(() => {
      el.focus();
      el.select();
    }, 60);
    return () => window.clearTimeout(id);
  }, [open, task]);

  if (!task) return null;

  const activeFolders = (folders ?? []).filter((f) => f.isActive !== false);

  function commitTitle() {
    if (task && title !== task.title && title.trim().length > 0) {
      updateTask.mutate({ taskId: task.id, title });
    }
  }
  function commitDescription() {
    if (task && description !== (task.description ?? "")) {
      updateTask.mutate({ taskId: task.id, description });
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="pbos-tasks pbos-tasks-drawer w-[min(560px,96vw)] sm:max-w-none p-0 gap-0 overflow-y-auto"
      >
        {/* ── Header ── */}
        <div className="px-5 py-4 border-b border-[var(--border-1)] bg-[var(--paper-1)]">
          <div className="flex items-center gap-2">
            <span className="tid-pill">{task.id.slice(0, 8).toUpperCase()}</span>
            <PhaseChip
              value={task.phase}
              onChange={(next: ProjectPhase) => updateTask.mutate({ taskId: task.id, phase: next })}
            />
          </div>
          <input
            ref={titleRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            className="mt-2 w-full text-[18px] font-semibold leading-tight text-[var(--text-1)] bg-transparent outline-none rounded-sm focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-1"
            placeholder="Task title"
          />
          <div className="flex items-center gap-2 mt-2">
            <StatusChip
              value={task.status as TaskStatus | null}
              onChange={(next) => updateTask.mutate({ taskId: task.id, status: next })}
            />
            <PriorityChip
              value={(task.priority ?? "medium") as TaskPriority}
              onChange={(next) => updateTask.mutate({ taskId: task.id, priority: next })}
            />
          </div>
        </div>

        {/* ── Properties grid ── */}
        <div className="px-5">
          <div className="props">
            <div className="prop-k">Trade</div>
            <div className="prop-v">
              <TradePicker
                value={task.assignedTradeId ?? null}
                trades={trades}
                onChange={(id) => updateTask.mutate({ taskId: task.id, assignedTradeId: id })}
              />
            </div>
            <div className="prop-k">Contact</div>
            <div className="prop-v">
              <ContactPicker
                value={task.assignedToContactId ?? null}
                contacts={contacts}
                onChange={(id) => updateTask.mutate({ taskId: task.id, assignedToContactId: id })}
              />
            </div>
            <div className="prop-k">Due date</div>
            <div className="prop-v">
              <DatePicker
                value={task.dueDate ?? null}
                onChange={(next) => updateTask.mutate({ taskId: task.id, dueDate: next ?? null })}
                ariaLabel="Due date"
                className="w-full max-w-[220px]"
              />
            </div>
            <div className="prop-k">Progress</div>
            <div className="prop-v">
              <div className="progress-cell flex-1">
                <span className={task.progress === 100 ? "bar done" : "bar"}>
                  <i style={{ width: `${task.progress ?? 0}%` }} />
                </span>
                <span className="mono">{task.progress ?? 0}%</span>
              </div>
            </div>
            <div className="prop-k">
              <Paperclip className="inline h-3 w-3 mr-1" />
              Evidence folder
            </div>
            <div className="prop-v">
              <Select
                value={task.defaultEvidenceFolderId ?? "none"}
                onValueChange={(val) =>
                  updateTask.mutate({
                    taskId: task.id,
                    defaultEvidenceFolderId: val === "none" ? null : val,
                  })
                }
              >
                <SelectTrigger className="h-auto border border-[var(--border-1)] bg-[var(--paper-0)] px-2 py-1 text-[12.5px] shadow-none focus:ring-0 w-full max-w-[260px]">
                  <SelectValue>
                    {task.defaultEvidenceFolderId
                      ? (activeFolders.find((f) => f.id === task.defaultEvidenceFolderId)
                          ?.fullPath ?? (folders === undefined ? "Loading…" : "Unknown folder"))
                      : "— No folder —"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="none">— No folder —</SelectItem>
                  {activeFolders.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.fullPath ?? f.id}
                    </SelectItem>
                  ))}
                  {folders !== undefined && folders.length === 0 ? (
                    <div className="px-3 py-2 text-[11.5px] text-[var(--text-4)] italic">
                      No folders configured for this project
                    </div>
                  ) : null}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Evidence Attachments ── */}
          <EvidenceSection
            projectId={projectId}
            taskId={task.id}
            folderId={task.defaultEvidenceFolderId}
          />

          {/* ── Description ── */}
          <div className="sec">
            <div className="sec-head">
              <span>Description</span>
            </div>
            <textarea
              className="desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={commitDescription}
              placeholder="Add a description…"
              rows={4}
            />
          </div>

          <ChecklistSection projectId={projectId} taskId={task.id} />
          <DependenciesSection projectId={projectId} taskId={task.id} allTasks={allTasks} />
          <ActivitySection
            projectId={projectId}
            taskId={task.id}
            onClose={() => onOpenChange(false)}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
